import { createSelector } from '@reduxjs/toolkit';
import { EntityStoreName } from 'app/enums/store';
import { TournamentBracketRoundEntity } from 'app/interfaces/entity';
import { AppState } from 'app/interfaces/store';
import { store } from 'redux/store';
import { entityAdapter } from './adapter';
import { useMemo } from 'react';

const tournamentBracketState = (state: AppState) => state[EntityStoreName.TournamentBracket];

const selectAll = entityAdapter.getSelectors<AppState>(
  (state) => state[EntityStoreName.TournamentBracket]
).selectAll;

const roundsSelector = createSelector(selectAll, (rounds: TournamentBracketRoundEntity[] = []) => {
  return rounds.filter((round) => !round.hidden).sort((a, b) => a?.order - b?.order);
});

const lastHiddenRoundSelector = createSelector(
  selectAll,
  (rounds: TournamentBracketRoundEntity[] = []) => {
    return rounds.reverse().find((round) => round.hidden);
  }
);

const hiddenRoundCountSelector = createSelector(
  selectAll,
  (rounds: TournamentBracketRoundEntity[] = []) => {
    return rounds.filter((round) => round.hidden).length;
  }
);

const selectSeedFactory = (matchId: number) =>
  createSelector(store.getState, (state) => {
    if (typeof matchId !== 'number') {
      return null;
    }

    return (
      entityAdapter
        .getSelectors<AppState>(tournamentBracketState)
        .selectAll(state)
        .flatMap((bracket) => bracket.seeds ?? [])
        .find((seed) => seed.id === matchId) ?? null
    );
  });

const selectSeedRoundFactory = (matchId: number) =>
  createSelector(store.getState, (state) => {
    if (typeof matchId !== 'number') {
      return '';
    }

    return (
      entityAdapter
        .getSelectors<AppState>(tournamentBracketState)
        .selectAll(state)
        .find((bracket) => bracket.seeds.find((seed) => seed.id === matchId))?.title ?? ''
    );
  });

export const tournamentBracketSelector = {
  rounds: roundsSelector,
  lastHiddenRound: lastHiddenRoundSelector,
  hiddenRoundCount: hiddenRoundCountSelector,
};

export const tournamentBracketSelectorFactory = {
  useSeed: (matchId: number) => useMemo(() => selectSeedFactory(matchId), [matchId]),
  useSeedRound: (matchId: number) => useMemo(() => selectSeedRoundFactory(matchId), [matchId]),
};
